import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import {
  Anchor,
  type AnchorProps,
  Button,
  type ButtonProps,
  NavLink,
  type NavLinkProps,
  Paper,
  type PaperProps,
  UnstyledButton,
  type UnstyledButtonProps,
} from '@mantine/core';
import { createLink, type LinkComponent } from '@tanstack/react-router';

/**
 * Mantine components that are typed router links, built once with TanStack's `createLink`
 * (https://tanstack.com/router/latest/docs/framework/react/guide/custom-link) so every call site
 * keeps `to` / `params` / `search` type-checked against the route tree.
 *
 * These replace two idioms that used to be scattered through the app: `renderRoot={(props) =>
 * <Link {...props} …/>}`, which creates a new root component on every render and loses Mantine's
 * polymorphic typing, and `component={Link}`, which type-checks but is spelt differently at every
 * site. One primitive per Mantine host, all anchors (`<a href>`), so the router's preloading,
 * active state and modifier-click behaviour are the same everywhere.
 *
 * Each host is wrapped in a `forwardRef` component with explicit anchor props: `createLink` needs
 * a component whose props are a plain intersection it can read the `ref` and DOM handlers from,
 * which Mantine's polymorphic factory components are not.
 */

type AnchorHostProps = AnchorProps & ComponentPropsWithoutRef<'a'>;

const AnchorHost = forwardRef<HTMLAnchorElement, AnchorHostProps>((props, ref) => (
  <Anchor ref={ref} {...props} />
));
AnchorHost.displayName = 'AnchorHost';
const CreatedAnchorLink = createLink(AnchorHost);

/**
 * @desc A router link styled as a Mantine `Anchor`. Links carry the hover state, not the
 * surrounding row or card (client decision: hover on buttons and links, not whole cards).
 */
export const AnchorLink: LinkComponent<typeof AnchorHost> = (props) => (
  <CreatedAnchorLink {...props} />
);

type ButtonHostProps = ButtonProps & ComponentPropsWithoutRef<'a'>;

const ButtonHost = forwardRef<HTMLAnchorElement, ButtonHostProps>((props, ref) => (
  <Button component="a" ref={ref} {...props} />
));
ButtonHost.displayName = 'ButtonHost';
const CreatedButtonLink = createLink(ButtonHost);

/** @desc A router link styled as a Mantine `Button` (every Button variant and section applies). */
export const ButtonLink: LinkComponent<typeof ButtonHost> = (props) => (
  <CreatedButtonLink {...props} />
);

type PaperHostProps = PaperProps & ComponentPropsWithoutRef<'a'>;

const PaperHost = forwardRef<HTMLAnchorElement, PaperHostProps>((props, ref) => (
  <Paper component="a" ref={ref} {...props} />
));
PaperHost.displayName = 'PaperHost';
const CreatedPaperLink = createLink(PaperHost);

/**
 * @desc A whole card as one router link — a Mantine `Paper` rendered as `<a>`. Use it where the
 * card has exactly one destination (a listing card, a news card); anything inside it that looks
 * like a control must be decorative (`ArrowMark`), since nested interactive content is invalid.
 */
export const PaperLink: LinkComponent<typeof PaperHost> = (props) => (
  <CreatedPaperLink {...props} />
);

type UnstyledButtonHostProps = UnstyledButtonProps & ComponentPropsWithoutRef<'a'>;

const UnstyledButtonHost = forwardRef<HTMLAnchorElement, UnstyledButtonHostProps>((props, ref) => (
  <UnstyledButton component="a" ref={ref} {...props} />
));
UnstyledButtonHost.displayName = 'UnstyledButtonHost';
const CreatedUnstyledButtonLink = createLink(UnstyledButtonHost);

/** @desc A router link with Mantine's button reset and nothing else — for custom-styled rows. */
export const UnstyledButtonLink: LinkComponent<typeof UnstyledButtonHost> = (props) => (
  <CreatedUnstyledButtonLink {...props} />
);

type NavLinkHostProps = NavLinkProps & ComponentPropsWithoutRef<'a'>;

const NavLinkHost = forwardRef<HTMLAnchorElement, NavLinkHostProps>((props, ref) => (
  <NavLink component="a" ref={ref} {...props} />
));
NavLinkHost.displayName = 'NavLinkHost';
const CreatedNavLinkLink = createLink(NavLinkHost);

/** @desc A router link as a Mantine `NavLink` row (the admin sidebar). */
export const NavLinkLink: LinkComponent<typeof NavLinkHost> = (props) => (
  <CreatedNavLinkLink {...props} />
);
